///
/// EarthBrowser
///
/// Created by Matt Giger on 10/26/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Desktop stand-in for a hidden cutout HUD (earthquake cross-section,
/// storm column): a one-line chip where the column was, carrying the
/// block's identity, a way to bring the panel back and the one explicit
/// way to close the block. Hiding the panel never closes the block or
/// moves focus off it; that is this chip's whole reason to exist.
///

import { cLayersPanelEdgeInset, useChromeInsets } from "@/hooks/useBottomChromeOffset"
import { cn } from "@/utils/utils"
import { ChevronRight, Cuboid, X } from "lucide-react"
import { type CSSProperties, type ReactNode } from "react"

/** Clearance under the top-left Layers / Search buttons (matches the HUD columns) */
const cChipTop = 64

export function CutoutHudChip({ kind, kindStyle, borderStyle, expandLabel, closeLabel, closeTitle, onExpand, onClose, children }: {
	/** Section word: "Cross-section" / "Storm column" */
	kind: string
	/** Color of the kind label and icon */
	kindStyle: CSSProperties
	/** Border color (with alpha), matching the HUD card */
	borderStyle: CSSProperties
	expandLabel: string
	closeLabel: string
	closeTitle: string
	onExpand: () => void
	onClose: () => void
	/** Identity line (magnitude + place, storm name) */
	children: ReactNode
}) {
	const chromeLeft = useChromeInsets().left
	return (
		<div
			className="fixed z-40 pointer-events-none"
			style={{ left: chromeLeft + cLayersPanelEdgeInset, top: cChipTop }}
		>
			<div
				className={cn(
					"pointer-events-auto flex items-center gap-2 pl-3 pr-1.5 py-1.5 max-w-[320px]",
					"bg-black/65 backdrop-blur-xl border rounded-full shadow-2xl",
					"motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-left-2 motion-safe:duration-200"
				)}
				style={borderStyle}
			>
				<button
					onClick={onExpand}
					className="flex items-center gap-2 min-w-0 text-left"
					aria-label={expandLabel}
					aria-expanded={false}
					title={expandLabel}
				>
					<Cuboid className="w-3.5 h-3.5 shrink-0" style={kindStyle} />
					<span className="text-[11px] font-semibold uppercase tracking-[0.18em] shrink-0" style={kindStyle}>{kind}</span>
					<span className="text-xs text-slate-200 truncate">{children}</span>
					<ChevronRight className="w-4 h-4 text-slate-400 shrink-0" aria-hidden />
				</button>
				<button
					onClick={onClose}
					className="w-6 h-6 flex items-center justify-center rounded-full text-slate-400 hover:text-white hover:bg-white/10 transition-colors shrink-0"
					aria-label={closeLabel}
					title={closeTitle}
				>
					<X className="w-3.5 h-3.5" />
				</button>
			</div>
		</div>
	)
}
